import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { API_URL } from './config';
import { AuthUser, usuariosApi } from './api/api';
import SessionsPage from './pages/SessionsPage';
import DispatchPage from './pages/DispatchPage';
import BasesPage from './pages/BasesPage';
import TemplatesPage from './pages/TemplatesPage';
import LeadsPendentesPage from './pages/LeadsPendentesPage';
import LoginPage from './pages/LoginPage';
import UsuariosPage from './pages/UsuariosPage';
import PagamentoPage from './pages/PagamentoPage';
import WebhookAdminPage from './pages/WebhookAdminPage';
import PlanosAdminPage from './pages/PlanosAdminPage';
import AssinantesPage from './pages/AssinantesPage';
import TesteAdminPage from './pages/TesteAdminPage';
import MinhaAssinaturaPage from './pages/MinhaAssinaturaPage';
import RelatorioRespostasPage from './pages/RelatorioRespostasPage';
import MaturacaoPage from './pages/MaturacaoPage';
import MaturadorCheckoutPage from './pages/MaturadorCheckoutPage';
import Sidebar from './components/Sidebar';
import MaturadorPopup from './components/MaturadorPopup';

export const CreateSessionContext = React.createContext<{ open: () => void }>({ open: () => {} });

export type { AuthUser };

const STORAGE_KEY = 'velox_user';

function ProtectedRoute({ path, user, children }: { path: string; user: AuthUser; children: React.ReactNode }) {
  const isAdmin = user.tipo === 'administrador';
  const allowed = isAdmin || user.telas.includes(path);
  if (!allowed) return <Navigate to={user.telas[0] ?? '/'} replace />;
  return <>{children}</>;
}


function TesteBanner({ expiracao, onUpgrade }: { expiracao: string; onUpgrade: () => void }) {
  const [tempo, setTempo] = useState('');
  const [expirado, setExpirado] = useState(false);

  useEffect(() => {
    if (expirado) {
      const t = setTimeout(() => onUpgrade(), 3000);
      return () => clearTimeout(t);
    }
  }, [expirado, onUpgrade]);

  useEffect(() => {
    const calcular = () => {
      const diff = new Date(expiracao).getTime() - Date.now();
      if (diff <= 0) { setTempo('Expirado'); setExpirado(true); return; }
      const dias = Math.floor(diff / (1000 * 60 * 60 * 24));
      const horas = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const min = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seg = Math.floor((diff % (1000 * 60)) / 1000);
      const pad = (n: number) => String(n).padStart(2, '0');
      if (dias > 0) setTempo(`${dias}d ${pad(horas)}h ${pad(min)}m ${pad(seg)}s`);
      else if (horas > 0) setTempo(`${pad(horas)}h ${pad(min)}m ${pad(seg)}s`);
      else if (min > 0) setTempo(`${pad(min)}m ${pad(seg)}s`);
      else setTempo(`${seg}s`);
    };
    calcular();
    const iv = setInterval(calcular, 1000);
    return () => clearInterval(iv);
  }, [expiracao]);

  if (expirado) {
    return (
      <div
        className="flex items-center justify-between gap-3 px-4 py-2 flex-shrink-0 text-white text-sm font-semibold"
        style={{ background: '#DC2626' }}
      >
        <span className="hidden sm:inline">Sandbox Mode</span>
        <span>Seu período de teste expirou — redirecionando para os planos...</span>
        <button
          onClick={onUpgrade}
          className="text-white border border-white rounded-btn px-4 py-1 text-sm font-semibold hover:opacity-70 transition-opacity"
          style={{ background: '#DC2626' }}
        >
          Ver planos agora
        </button>
      </div>
    );
  }

  return (
    <div
      className="flex items-center justify-between gap-3 px-4 py-2 flex-shrink-0 text-white text-sm font-semibold"
      style={{ background: '#FEA94C' }}
    >
      <span className="hidden sm:inline">Sandbox Mode</span>
      <span className="flex items-center gap-2 text-center">
        Seu acesso expira em <span className="bg-white/20 rounded-full px-2 py-0.5 text-xs font-bold">{tempo}</span>
        <span className="hidden md:inline opacity-80">— não perca seu acesso</span>
      </span>
      <button
        onClick={onUpgrade}
        className="rounded-btn px-4 py-1 text-sm font-semibold hover:opacity-80 transition-opacity"
        style={{ background: '#22C55E', color: '#FFFFFF' }}
      >
        Fazer upgrade
      </button>
    </div>
  );
}

function MensalidadeBanner({ diasRestantes, onRenovar, isPreview }: { diasRestantes: number; onRenovar: () => void; isPreview?: boolean }) {
  return (
    <div
      className="flex items-center justify-between gap-3 px-4 py-2 flex-shrink-0 text-white text-sm font-semibold"
      style={{ background: '#FEA94C' }}
    >
      <span className="hidden sm:inline">{isPreview ? 'Aviso (prévia)' : 'Mensalidade vence em breve'}</span>
      <span className="flex items-center gap-2 text-center">
        Sua mensalidade vence em
        <span className="bg-white/20 rounded-full px-2 py-0.5 text-xs font-bold">
          {diasRestantes} {diasRestantes === 1 ? 'dia' : 'dias'}
        </span>
        <span className="hidden md:inline opacity-80">— renove para não perder o acesso</span>
      </span>
      <button
        onClick={onRenovar}
        className="rounded-btn px-4 py-1 text-sm font-semibold hover:opacity-80 transition-opacity"
        style={{ background: '#22C55E', color: '#FFFFFF' }}
      >
        Renovar agora
      </button>
    </div>
  );
}

const ADMIN_RENEW_DISMISSED_KEY = 'velox_admin_renew_preview_dismissed_v2';
const PREVIEW_DIAS_RESTANTES_ADMIN = 7;

function AppLayout({ user, onLogout, createModalOpen, setCreateModalOpen, onIrParaPagamento }: {
  user: AuthUser;
  onLogout: () => void;
  createModalOpen: boolean;
  setCreateModalOpen: (v: boolean) => void;
  onIrParaPagamento: () => void;
}) {
  const [dropOpen, setDropOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [saldoDisparos, setSaldoDisparos] = useState<{ limiteMensal: number; enviadosMes: number; saldoRestante: number | null } | null>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket | null>(null);
  const saldoRefreshTimeoutRef = useRef<number | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) setDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (user.tipo === 'administrador') return;

    let ativo = true;
    const carregarSaldo = async () => {
      try {
        const { data } = await usuariosApi.getSaldoDisparosMes();
        if (ativo) setSaldoDisparos(data);
      } catch {}
    };

    carregarSaldo();
    const agendarRefreshSaldo = () => {
      if (saldoRefreshTimeoutRef.current) {
        window.clearTimeout(saldoRefreshTimeoutRef.current);
      }
      saldoRefreshTimeoutRef.current = window.setTimeout(() => {
        carregarSaldo();
      }, 350);
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) carregarSaldo();
    };

    socketRef.current = io(API_URL, { transports: ['websocket'] });
    socketRef.current.on('dispatch:progress', agendarRefreshSaldo);
    socketRef.current.on('message:status', agendarRefreshSaldo);
    socketRef.current.on('connect', carregarSaldo);

    const interval = setInterval(carregarSaldo, 15000);
    window.addEventListener('focus', carregarSaldo);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      ativo = false;
      if (saldoRefreshTimeoutRef.current) {
        window.clearTimeout(saldoRefreshTimeoutRef.current);
        saldoRefreshTimeoutRef.current = null;
      }
      window.removeEventListener('focus', carregarSaldo);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [user.id, user.tipo]);

  const isAdmin = user.tipo === 'administrador';
  const telasPermitidas = isAdmin ? null : user.telas;
  const limiteMensal = saldoDisparos?.limiteMensal ?? 0;
  const saldoRestante = saldoDisparos?.saldoRestante ?? null;
  const limiteAtingido = !isAdmin && saldoDisparos !== null && limiteMensal > 0 && saldoRestante === 0;

  const showSandbox = !!user.modoTeste;
  // Periodo de teste libera o plano Avancado completo, incluindo o maturador
  const podeUsarMaturador = isAdmin || !!user.temMaturador || !!user.modoTeste;
  const [showMaturadorPopup, setShowMaturadorPopup] = useState(
    !podeUsarMaturador && !user.popupMaturadorVisto,
  );

  const [adminMensalidadeDismissed, setAdminMensalidadeDismissed] = useState(() => {
    try { return localStorage.getItem(ADMIN_RENEW_DISMISSED_KEY) === '1'; } catch { return false; }
  });

  const diasRestantesAssinatura = (() => {
    if (!user.assinaturaExpiracao) return null;
    const diff = new Date(user.assinaturaExpiracao).getTime() - Date.now();
    if (Number.isNaN(diff)) return null;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  })();

  const mostrarBannerMensalidade = !showSandbox && (
    isAdmin
      ? !adminMensalidadeDismissed
      : (diasRestantesAssinatura !== null && diasRestantesAssinatura > 0 && diasRestantesAssinatura <= 10)
  );

  const handleRenovarMensalidade = () => {
    if (isAdmin) {
      try { localStorage.setItem(ADMIN_RENEW_DISMISSED_KEY, '1'); } catch {}
      setAdminMensalidadeDismissed(true);
    }
    onIrParaPagamento();
  };

  return (
    <div
      className="h-screen flex flex-col overflow-hidden text-ink-primary"
      style={{ background: (showSandbox || mostrarBannerMensalidade) ? '#FEA94C' : '#F3F4F6' }}
    >
      {showMaturadorPopup && (
        <MaturadorPopup
          onClose={() => setShowMaturadorPopup(false)}
          onUpgrade={() => { setShowMaturadorPopup(false); navigate('/checkout/maturador'); }}
        />
      )}

      {showSandbox && user.assinaturaExpiracao && (
        <TesteBanner expiracao={user.assinaturaExpiracao} onUpgrade={onIrParaPagamento} />
      )}

      {mostrarBannerMensalidade && (
        <MensalidadeBanner
          diasRestantes={isAdmin ? PREVIEW_DIAS_RESTANTES_ADMIN : (diasRestantesAssinatura ?? 0)}
          onRenovar={handleRenovarMensalidade}
          isPreview={isAdmin}
        />
      )}

      <div
        className="flex-1 flex w-full overflow-hidden"
        style={{ background: '#F3F4F6' }}
      >
        <Sidebar
          onCreateSession={() => setCreateModalOpen(true)}
          telasPermitidas={telasPermitidas}
          isAdmin={isAdmin}
          mobileOpen={mobileSidebarOpen}
          onCloseMobile={() => setMobileSidebarOpen(false)}
          userName={user.nomeCompleto}
          userType={user.tipo}
          onLogout={onLogout}
          temMaturador={podeUsarMaturador}
        />

        <div className="flex-1 flex min-w-0 flex-col overflow-hidden">
        {/* Navbar top (tema claro) — só sobre o conteúdo, respeita a sidebar */}
        <header className="flex items-center gap-3 flex-shrink-0 h-14 px-4 bg-white border-b border-gray-200">
          <button
            type="button"
            onClick={() => setMobileSidebarOpen(true)}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100"
            aria-label="Abrir menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          <div className="ml-auto flex items-center gap-2">
          <a
            href="https://wa.me/554399108188"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-btn text-sm font-medium text-ink-primary hover:bg-gray-100 transition-colors"
            title="Falar com o suporte no WhatsApp"
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24" style={{ color: '#25D366' }}>
              <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
            </svg>
            <span className="hidden sm:inline">Suporte</span>
          </a>

          <div className="relative" ref={dropRef}>
            <button
              type="button"
              onClick={() => setDropOpen(v => !v)}
              className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-3 py-1.5 transition-all"
            >
              <div className="flex flex-col items-end min-w-0 text-right">
                <span className="text-sm text-gray-900 font-semibold leading-tight whitespace-nowrap">{user.nomeCompleto.split(' ')[0]}</span>
                {limiteAtingido ? (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onIrParaPagamento(); }}
                    className="text-[11px] font-semibold leading-tight whitespace-nowrap mt-0.5 underline hover:opacity-80"
                    style={{ color: '#DC2626' }}
                    title="Clique para fazer upgrade"
                  >
                    Limite mensal atingido — fazer upgrade
                  </button>
                ) : (
                  <span
                    className="text-[11px] font-semibold leading-tight whitespace-nowrap mt-0.5"
                    style={{ color: '#059669' }}
                  >
                    {isAdmin
                      ? 'Disparos ilimitados'
                      : saldoDisparos == null
                        ? 'Carregando saldo...'
                        : saldoDisparos.saldoRestante === null
                          ? 'Disparos ilimitados'
                          : `${saldoDisparos.saldoRestante.toLocaleString('pt-BR')} de ${saldoDisparos.limiteMensal.toLocaleString('pt-BR')} disparos no mês`}
                  </span>
                )}
              </div>
              <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${dropOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {dropOpen && (
              <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-50">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-900 truncate">{user.nomeCompleto}</p>
                  <p className="text-xs text-gray-400 mt-0.5 capitalize">{user.tipo}</p>
                </div>
                <button
                  type="button"
                  onClick={onLogout}
                  className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-red-500 hover:bg-red-50 transition-colors"
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Sair
                </button>
              </div>
            )}
          </div>
          </div>
        </header>

        <main className="flex-1 min-h-0 overflow-y-auto bg-white">
          <div className="p-6">
            <Routes>
              <Route path="/" element={
                <ProtectedRoute path="/" user={user}>
                  <SessionsPage createModalOpen={createModalOpen} setCreateModalOpen={setCreateModalOpen} maxSessoes={user.maxSessoes ?? 0} isAdmin={user.tipo === 'administrador'} onIrParaPagamento={onIrParaPagamento} />
                </ProtectedRoute>
              } />
              <Route path="/disparador" element={
                <ProtectedRoute path="/disparador" user={user}>
                  <DispatchPage />
                </ProtectedRoute>
              } />
              <Route path="/bases" element={
                <ProtectedRoute path="/bases" user={user}>
                  <BasesPage />
                </ProtectedRoute>
              } />
              <Route path="/templates" element={
                <ProtectedRoute path="/templates" user={user}>
                  <TemplatesPage />
                </ProtectedRoute>
              } />
              <Route path="/relatorio-respostas" element={
                isAdmin
                  ? <RelatorioRespostasPage />
                  : <Navigate to="/" replace />
              } />
              <Route path="/respostas" element={<LeadsPendentesPage />} />
              <Route path="/leads-pendentes" element={<Navigate to="/respostas" replace />} />
              <Route path="/leads" element={<Navigate to="/respostas" replace />} />
              <Route path="/categorias" element={<Navigate to="/bases" replace />} />
              <Route path="/usuarios" element={
                isAdmin
                  ? <UsuariosPage currentUser={user} />
                  : <Navigate to="/" replace />
              } />
              <Route path="/webhook-admin" element={
                isAdmin
                  ? <WebhookAdminPage />
                  : <Navigate to="/" replace />
              } />
              <Route path="/planos" element={
                isAdmin
                  ? <PlanosAdminPage />
                  : <Navigate to="/" replace />
              } />
              <Route path="/assinantes" element={
                isAdmin
                  ? <AssinantesPage />
                  : <Navigate to="/" replace />
              } />
              <Route path="/config-teste" element={
                isAdmin
                  ? <TesteAdminPage />
                  : <Navigate to="/" replace />
              } />
              <Route path="/minha-assinatura" element={
                !isAdmin
                  ? <MinhaAssinaturaPage onIrParaPagamento={onIrParaPagamento} />
                  : <Navigate to="/" replace />
              } />
              <Route path="/checkout/maturador" element={
                podeUsarMaturador
                  ? <Navigate to="/maturacao" replace />
                  : <MaturadorCheckoutPage
                      cpfDoUsuario={null}
                      onApproved={() => {
                        // forca refetch do user pelo validate-token (loop em App ja faz polling)
                        navigate('/maturacao');
                      }}
                      onVoltar={() => navigate(-1)}
                    />
              } />
              <Route path="/maturacao" element={
                podeUsarMaturador
                  ? <MaturacaoPage />
                  : <Navigate to="/" replace />
              } />
              <Route path="*" element={<Navigate to={isAdmin ? '/' : (user.telas[0] ?? '/')} replace />} />
            </Routes>
          </div>
        </main>
        </div>
      </div>
    </div>
  );
}

function isAssinaturaAtiva(user: AuthUser): boolean {
  if (user.tipo === 'administrador') return true;
  if (!user.assinaturaExpiracao) return false;
  return new Date(user.assinaturaExpiracao) > new Date();
}

export default function App() {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [forcarPagamento, setForcarPagamento] = useState(false);
  const loggingOutRef = useRef(false);
  const [user, setUser] = useState<AuthUser | null>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const handleLogin = (u: AuthUser) => {
    loggingOutRef.current = false;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
    setUser(u);
  };

  const handleLogout = useCallback(() => {
    loggingOutRef.current = true;
    localStorage.removeItem(STORAGE_KEY);
    window.history.replaceState(null, '', '/login');
    setUser(null);
  }, []);

  const handleSubscriptionActive = useCallback((expiracao: string) => {
    setForcarPagamento(false);
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, assinaturaExpiracao: expiracao, modoTeste: false };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
    // Redireciona para tela de assinatura após pagamento aprovado
    window.history.replaceState(null, '', '/minha-assinatura');
  }, []);

  useEffect(() => {
    if (!user?.tokenSessao) return;
    let ativo = true;
    const userIdAtual = user.id;
    const tokenAtual = user.tokenSessao;

    const check = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/usuarios/validate-token`, {
          params: { userId: userIdAtual, token: tokenAtual },
        });
        if (!ativo || loggingOutRef.current) return;

        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return;

        const current = JSON.parse(stored);
        if (current?.id !== userIdAtual || current?.tokenSessao !== tokenAtual) return;

        if (data?.user) {
          const updated = { ...user, ...data.user };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
          setUser(updated);
        }
      } catch (error) {
        if (!ativo || loggingOutRef.current) return;
        if (axios.isAxiosError(error)) {
          const status = error.response?.status;
          if (status === 401 || status === 403) {
            handleLogout();
          }
          return;
        }
      }
    };
    check();
    const interval = setInterval(check, 20000);
    return () => {
      ativo = false;
      clearInterval(interval);
    };
  }, [user, handleLogout]);

  if (!user) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="*" element={<LoginPage onLogin={handleLogin} />} />
        </Routes>
      </BrowserRouter>
    );
  }

  const assinaturaAtiva = isAssinaturaAtiva(user);

  if (!assinaturaAtiva || forcarPagamento) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/pagamento/sucesso" element={
            <PagamentoPage subPage="sucesso" nomeCompleto={user.nomeCompleto}
              onSubscriptionActive={handleSubscriptionActive} onLogout={handleLogout} />
          } />
          <Route path="/pagamento/falha" element={
            <PagamentoPage subPage="falha" nomeCompleto={user.nomeCompleto}
              onSubscriptionActive={handleSubscriptionActive} onLogout={handleLogout} />
          } />
          <Route path="/pagamento/pendente" element={
            <PagamentoPage subPage="pendente" nomeCompleto={user.nomeCompleto}
              onSubscriptionActive={handleSubscriptionActive} onLogout={handleLogout} />
          } />
          <Route path="*" element={
            <PagamentoPage subPage="wall" nomeCompleto={user.nomeCompleto}
              onSubscriptionActive={handleSubscriptionActive} onLogout={handleLogout}
              onVoltar={forcarPagamento ? () => setForcarPagamento(false) : undefined} />
          } />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <CreateSessionContext.Provider value={{ open: () => setCreateModalOpen(true) }}>
      <BrowserRouter>
        <AppLayout
          user={user}
          onLogout={handleLogout}
          createModalOpen={createModalOpen}
          setCreateModalOpen={setCreateModalOpen}
          onIrParaPagamento={() => setForcarPagamento(true)}
        />
      </BrowserRouter>
    </CreateSessionContext.Provider>
  );
}
